/* eslint-disable react/destructuring-assignment */
import { clsx } from 'clsx';
import React, { Children, ReactNode } from 'react';
import { Button, ButtonProps, Link, LinkProps } from 'react-aria-components';

import { isIconElement } from '@/utils/isIconElement';
import styles from './ActionChip.module.scss';

type BaseActionChipProps = {
  /** Whether the `ActionChip` is disabled. */
  isDisabled?: boolean;

  /** Handler called when the press is released over the `ActionChip`. */
  onPress?: ButtonProps['onPress'];

  /**
   * The size of the `ActionChip`.
   * @default 'small'
   */
  size?: 'compact' | 'small' | 'medium';

  /**
   * The chip variant.
   * @default "primary"
   */
  variant?: 'primary' | 'ghost';

  /** The inline style for the chip. */
  style?: React.CSSProperties;

  /** The CSS `className` for the chip. */
  className?: string;
};

// className is a string type and style is a CSSProperties type because ActionChip can render either a RAC Button or RAC Link, and their render props don't match up.

type ActionChipButtonProps = BaseActionChipProps &
  Pick<ButtonProps, 'children'> & {
    // TypeScript needs to know that this _never_ takes an `href`, so that it
    // can use the presence of `href` as the discriminant for the type union
    // `ActionChipProps`
    href?: never;
    isSelected?: never;
  };

type ActionChipLinkProps = BaseActionChipProps &
  Pick<LinkProps, 'children'> & {
    /** A URL to link. When passed, `ActionChip` will be rendered as a Link instead of a button.  */
    href: string;
    /** Hints at the human language of the linked URL. Only used if `href` prop is passed. */
    hrefLang?: LinkProps['hrefLang'];
    /** The target window. Only used if `href` prop is passed. */
    target?: LinkProps['target'];
    // TypeScript needs to know that Button _never_ takes an `href`, so that it
    // can use the presence of `href` as the discriminant for the type union
    // `ActionChipProps`
    isSelected?: never;
  };

// type ActionChipToggleButtonProps = BaseActionChipProps &
//   Pick<ToggleButtonProps, 'children'> & {
//     // eslint-disable-next-line react/no-unused-prop-types
//     /** Whether the chip is selected or not */
//     isSelected: boolean;
//     // TypeScript needs to know that this _never_ takes an `href`, so that it
//     // can use the presence of `href` as the discriminant for the type union
//     // `ActionChipProps`
//     href?: never;
//   };

export type ActionChipProps = ActionChipButtonProps | ActionChipLinkProps;
// | ActionChipToggleButtonProps;

function isLinkProps(props: ActionChipProps): props is ActionChipLinkProps {
  return 'href' in props && typeof props.href === 'string';
}

// TODO: Add toggle button chip
// function isToggleButtonProps(
//   props: ActionChipProps
// ): props is ActionChipToggleButtonProps {
//   return 'isSelected' in props && typeof props.isSelected === 'boolean';
// }

/**
 * Action Chips (also known as Assist Chips) are used when the user needs to
 * take an action in a temporary panel that depends on the user’s interaction.
 * Very similar to Buttons and Icon Buttons, except they are not supposed to be
 * static on the page.
 *
 * ---
 *
 * When passed an `href` prop, it will render as a link instead of a button.
 *
 * ---
 *
 * When passed `isSelected`, it will behave as a toggle button. Wrap with
 * `ActionChipToggleGroup` to control selected state between multiple items.
 *
 * ---
 *
 * @see ActionChipToggleGroup
 */
export const ActionChip = (props: ActionChipProps) => {
  if (isLinkProps(props)) {
    return <ActionChipLink {...props} />;
  }
  // if (isToggleButtonProps(props)) {
  //   return <ActionChipToggleButton {...props} />;
  // }
  return <ActionChipButton {...props} />;
};

const ActionChipButton = ({
  isDisabled = false,
  onPress,
  size = 'compact',
  style,
  variant = 'primary',
  children,
  className,
}: ActionChipButtonProps) => {
  return (
    <Button
      className={clsx(styles.root, className)}
      data-variant={variant}
      data-size={size}
      data-zds
      isDisabled={isDisabled}
      onPress={onPress}
      style={style}
    >
      {(renderProps) => {
        const kids = Children.toArray(
          typeof children === 'function' ? children(renderProps) : children
        );
        return <ChildrenWrapperForIcons>{kids}</ChildrenWrapperForIcons>;
      }}
    </Button>
  );
};

// TODO: We still need to add support for "selected" action chips. There's some
// issues with the implicit choice between underlying components (i.e. link,
// button, or toggle button), and how things ought to work with using React Aria
// ToggleButton.

// const ActionChipToggleButton = ({
//   isDisabled,
//   onPress,
//   size,
//   style,
//   variant = 'primary',
//   children,
//   className,
// }: ActionChipToggleButtonProps) => {
//   return (
//     <ToggleButton
//       className={clsx(styles.root, className)}
//       data-size={size}
//       data-zds
//       isDisabled={isDisabled}
//       onPress={onPress}
//       style={style}
//       data-variant={variant}
//     >
//       {(renderProps) =>
//         typeof children === 'function' ? children(renderProps) : children
//       }
//     </ToggleButton>
//   );
// };

const ActionChipLink = ({
  href,
  hrefLang,
  isDisabled = false,
  onPress,
  size = 'compact',
  style,
  target,
  variant = 'primary',
  children,
  className,
}: ActionChipLinkProps) => {
  return (
    <Link
      className={clsx(styles.root, className)}
      data-size={size}
      data-zds
      href={href}
      hrefLang={hrefLang}
      isDisabled={isDisabled}
      onPress={onPress}
      style={style}
      target={target}
      data-variant={variant}
    >
      {(renderProps) => {
        const kids = Children.toArray(
          typeof children === 'function' ? children(renderProps) : children
        );
        return <ChildrenWrapperForIcons>{kids}</ChildrenWrapperForIcons>;
      }}
    </Link>
  );
};

type ChildrenWrapperForIconsProps = {
  children: ReactNode[];
};

/**
 * Wrapper for children that lets us read whether the icon is text-only;
 * icon-only; or icon+text, so that we can adjust padding appropriately.
 */
const ChildrenWrapperForIcons = ({
  children,
}: ChildrenWrapperForIconsProps) => {
  let format = 'text';
  if (children.length === 1 && isIconElement(children[0])) {
    format = 'icon';
  } else if (children.length > 1 && isIconElement(children[0])) {
    format = 'icon-text';
  }
  return (
    <div className={styles.children} data-children-format={format}>
      {children}
    </div>
  );
};

export { ActionChipIcon, type ActionChipIconProps } from './ActionChipIcon';
